import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Handshake, History, Star } from 'lucide-react';
import { Badge, Button, ConfirmModal, Modal, Select, TextInput, PromptModal } from '@kardume/ui';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, extractErrorMessage, formatDate } from '@kardume/services';
import type {
  CategoryDto,
  FeatureDensityDto,
  LocalityDto,
  MerchantDto,
  MerchantFeatureDto,
  MerchantFeatureScope,
  MerchantPartnershipStatus,
  PartnershipDto,
  PartnershipEventDto,
  Tone,
} from '@kardume/types';

/**
 * Bloco "Parceria" do drawer do lojista (card 226, regras §37).
 *
 * É a única porta pela qual o selo `✓ Parceiro Kardume` acende e apaga. Antes
 * dele o estado só mudaria por SQL - e um selo que muda por SQL não tem motivo,
 * não tem autor e não tem histórico, que são exatamente as três coisas que o
 * lojista pergunta quando liga querendo saber por que perdeu o dele.
 *
 * A tela **não** conhece a máquina de estados: as ações disponíveis vêm em
 * `allowedTransitions`. Duplicar a regra aqui seria garantir que ela saísse de
 * sincronia na primeira mudança, e o sintoma seria um botão que existe e
 * responde 400.
 */
const STATUS_LABELS: Record<MerchantPartnershipStatus, string> = {
  NONE: 'Sem parceria',
  PENDING: 'Em negociação',
  ACTIVE: 'Parceira',
  SUSPENDED: 'Suspensa',
  EXPIRED: 'Expirada',
  TERMINATED: 'Encerrada',
};

const STATUS_TONES: Record<MerchantPartnershipStatus, Tone> = {
  NONE: 'secondary',
  PENDING: 'info',
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  EXPIRED: 'secondary',
  TERMINATED: 'danger',
};

/** O verbo de cada transição, do ponto de vista de quem opera. */
const ACTION_LABELS: Record<MerchantPartnershipStatus, string> = {
  NONE: 'Zerar',
  PENDING: 'Marcar em negociação',
  ACTIVE: 'Ativar',
  SUSPENDED: 'Suspender',
  EXPIRED: 'Marcar expirada',
  TERMINATED: 'Encerrar',
};

/** As que tiram o selo do ar pedem confirmação com tom destrutivo. */
const DESTRUCTIVE: MerchantPartnershipStatus[] = ['SUSPENDED', 'EXPIRED', 'TERMINATED'];

/**
 * O contexto de cada escopo, do ponto de vista de quem opera.
 *
 * "Destaque em Toledo" e "destaque em Gastronomia" são coisas diferentes de
 * "destaque no Kardume inteiro", e a diferença é o que impede o operador de
 * dar exposição na plataforma toda achando que deu numa cidade só.
 */
const SCOPE_LABELS: Record<MerchantFeatureScope, string> = {
  PLATFORM: 'Plataforma inteira',
  LOCALITY: 'Uma cidade',
  CATEGORY: 'Uma categoria',
};

/** O formulário vazio. `PLATFORM` é o default por ser o único sem contexto a escolher. */
const EMPTY_FEATURE = {
  scope: 'PLATFORM' as MerchantFeatureScope,
  localityId: '',
  categoryId: '',
  startsAt: '',
  endsAt: '',
  reason: '',
};

/**
 * `datetime-local` devolve hora de parede, sem fuso. O core resolve vigência em
 * `OffsetDateTime`, então o instante precisa ser absoluto - `toISOString()`
 * converte usando o fuso do navegador do operador, que é o que ele quis dizer
 * ao digitar "18h".
 *
 * Sem isso, um destaque agendado para as 18h de Toledo começaria às 15h ou às
 * 21h dependendo de onde o servidor estivesse.
 */
function toInstant(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

interface Props {
  merchant: MerchantDto;
  /** `false` esconde as ações - sem `PARTNERSHIP_MANAGE` os botões não existem. */
  canManage: boolean;
}

const MerchantPartnershipSection: React.FC<Props> = ({ merchant, canManage }) => {
  const { showNotification } = useNotification();
  const [partnership, setPartnership] = useState<PartnershipDto | null>(null);
  const [history, setHistory] = useState<PartnershipEventDto[]>([]);
  const [features, setFeatures] = useState<MerchantFeatureDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<MerchantPartnershipStatus | null>(null);
  const [saving, setSaving] = useState(false);

  // Destaques (card 237). O formulário só abre com parceria ativa, então as
  // listas de contexto são carregadas na abertura - e não no load do drawer,
  // que é a tela que mais abre e menos precisa delas.
  const [featureForm, setFeatureForm] = useState<typeof EMPTY_FEATURE | null>(null);
  const [localities, setLocalities] = useState<LocalityDto[]>([]);
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [endingFeature, setEndingFeature] = useState<MerchantFeatureDto | null>(null);

  /**
   * Quantos destaques já disputam a vitrine que este vai disputar (card 262).
   *
   * É o número que falta ao operador na hora de prometer exposição. Sem ele,
   * três pessoas do comercial vendem destaque na mesma cidade na mesma semana,
   * cada uma sem saber das outras; o motor de posicionamento tem teto e
   * simplesmente não promove a maioria, e a descoberta vem pela reclamação do
   * parceiro - exposição vendida e não entregue.
   *
   * `null` é "ainda não dá para perguntar": escopo de cidade sem cidade
   * escolhida não tem contexto nenhum para contar.
   */
  const [density, setDensity] = useState<FeatureDensityDto | null>(null);

  const merchantId = merchant.id;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.all([
      backofficeApi.getPartnership(merchantId),
      backofficeApi.listPartnershipHistory(merchantId),
      backofficeApi.listMerchantFeatures(merchantId),
    ])
      .then(([current, events, active]) => {
        setPartnership(current);
        setHistory(events);
        setFeatures(active);
      })
      .catch((e) => setError(extractErrorMessage(e, 'Não foi possível carregar a parceria.')))
      .finally(() => setLoading(false));
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = (reason: string) => {
    if (!pending) return;
    setSaving(true);
    backofficeApi
      .changePartnership(merchantId, { status: pending, reason })
      .then(() => {
        showNotification('Parceria atualizada.', 'success');
        setPending(null);
        return load();
      })
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível atualizar a parceria.'), 'error'))
      .finally(() => setSaving(false));
  };

  const featureScope = featureForm?.scope;
  const featureLocalityId = featureForm?.localityId;
  const featureCategoryId = featureForm?.categoryId;

  useEffect(() => {
    if (!featureScope) {
      setDensity(null);
      return;
    }
    // Contexto incompleto não é contexto: perguntar a lotação de "uma cidade"
    // sem dizer qual devolveria só os destaques de plataforma, e o operador
    // leria esse número como se fosse o da cidade que ele ainda vai escolher.
    if (featureScope === 'LOCALITY' && !featureLocalityId) {
      setDensity(null);
      return;
    }
    if (featureScope === 'CATEGORY' && !featureCategoryId) {
      setDensity(null);
      return;
    }
    let active = true;
    backofficeApi
      .getFeatureDensity(featureScope, {
        localityId: featureLocalityId,
        categoryId: featureCategoryId,
      })
      .then((result) => {
        if (active) setDensity(result);
      })
      // O aviso é opcional; falhar ao carregá-lo não pode impedir a criação.
      // Um formulário que trava porque a dica não veio troca um problema raro
      // por um problema garantido.
      .catch(() => {
        if (active) setDensity(null);
      });
    return () => {
      active = false;
    };
  }, [featureScope, featureLocalityId, featureCategoryId]);

  const openFeatureForm = () => {
    setFeatureForm(EMPTY_FEATURE);
    // Falha ao carregar contexto não impede destaque de plataforma, que é o
    // único que não precisa de nenhum dos dois.
    backofficeApi.listLocalities({ size: 200, active: true })
      .then((page) => setLocalities(page.content))
      .catch(() => setLocalities([]));
    backofficeApi.listCategories({ size: 200 })
      .then((page) => setCategories(page.content))
      .catch(() => setCategories([]));
  };

  const submitFeature = () => {
    if (!featureForm) return;
    setSaving(true);
    backofficeApi
      .createMerchantFeature(merchantId, {
        scope: featureForm.scope,
        reason: featureForm.reason.trim(),
        localityId: featureForm.scope === 'LOCALITY' ? featureForm.localityId || null : null,
        categoryId: featureForm.scope === 'CATEGORY' ? featureForm.categoryId || null : null,
        startsAt: toInstant(featureForm.startsAt),
        endsAt: toInstant(featureForm.endsAt),
      })
      .then(() => {
        showNotification('Destaque criado.', 'success');
        setFeatureForm(null);
        setDensity(null);
        return load();
      })
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível criar o destaque.'), 'error'))
      .finally(() => setSaving(false));
  };

  const confirmEndFeature = () => {
    if (!endingFeature) return;
    setSaving(true);
    backofficeApi
      .endMerchantFeature(merchantId, endingFeature.id)
      .then(() => {
        showNotification('Destaque encerrado.', 'success');
        setEndingFeature(null);
        return load();
      })
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível encerrar o destaque.'), 'error'))
      .finally(() => setSaving(false));
  };

  if (loading) return <Section><h4><Handshake size={16} /> Parceria</h4><Muted>Carregando…</Muted></Section>;
  if (error || !partnership) {
    return (
      <Section>
        <h4><Handshake size={16} /> Parceria</h4>
        <Muted>{error ?? 'Parceria indisponível.'}</Muted>
      </Section>
    );
  }

  // Sugestão, nunca automatismo (§37.2). Tirar o selo de um parceiro de
  // cortesia porque a assinatura venceu seria derivar parceria de contrato -
  // exatamente o que a estratégia comercial decidiu não fazer.
  const suggestSuspend =
    partnership.partnershipStatus === 'ACTIVE' &&
    (merchant.billingStatus ?? '').toLowerCase().includes('pendente');

  return (
    <>
      <Section>
        <h4>
          <Handshake size={16} /> Parceria
        </h4>

        <Field>
          <div className="k">Status</div>
          <div className="v">
            <Badge tone={STATUS_TONES[partnership.partnershipStatus]}>
              {STATUS_LABELS[partnership.partnershipStatus]}
            </Badge>{' '}
            {partnership.partner && <Badge tone="success">✓ Selo visível</Badge>}
          </div>
        </Field>
        <Field>
          <div className="k">Parceira desde</div>
          <div className="v">
            {partnership.partnershipStartedAt ? formatDate(partnership.partnershipStartedAt) : '-'}
          </div>
        </Field>
        {partnership.partnershipEndedAt && (
          <Field>
            <div className="k">Encerrada em</div>
            <div className="v">{formatDate(partnership.partnershipEndedAt)}</div>
          </Field>
        )}
        <Field>
          <div className="k">Perfil</div>
          <div className="v">
            {partnership.claimed ? 'Reivindicado pela empresa' : 'Administrado pelo Kardume'}
          </div>
        </Field>

        {suggestSuspend && (
          <Suggestion>
            Assinatura com pagamento pendente. Se for o caso, suspenda a parceria - o selo
            <strong> não </strong>
            é retirado sozinho: parceiro de cortesia não tem contrato, e um automatismo
            silencioso apagaria o selo dele junto.
          </Suggestion>
        )}

        {canManage && (
          <Actions>
            {partnership.allowedTransitions.map((next) => (
              <Button
                key={next}
                variant={DESTRUCTIVE.includes(next) ? 'danger' : 'secondary'}
                onClick={() => setPending(next)}
              >
                {ACTION_LABELS[next]}
              </Button>
            ))}
          </Actions>
        )}
      </Section>

      <Section>
        <h4>
          <Star size={16} /> Destaques
        </h4>

        {features.length === 0 && <Muted>Nenhum destaque vigente.</Muted>}

        {features.map((f) => (
          <FeatureRow key={f.id}>
            <div>
              <div className="k">
                {f.scope === 'LOCALITY'
                  ? `Cidade · ${f.localityName ?? '-'}`
                  : f.scope === 'CATEGORY'
                    ? `Categoria · ${f.categoryName ?? '-'}`
                    : 'Plataforma'}
              </div>
              <div className="v">
                {f.reason}
                <Muted>
                  {formatDate(f.startsAt)} - {f.endsAt ? formatDate(f.endsAt) : 'sem data de fim'}
                </Muted>
              </div>
            </div>
            {canManage && (
              <Button variant="ghost" onClick={() => setEndingFeature(f)}>
                Encerrar agora
              </Button>
            )}
          </FeatureRow>
        ))}

        {/* Destaque é o nível de cima da parceria. Sem parceria ativa o botão
            não existe - e o motivo aparece no lugar, porque um botão que some
            sem explicação faz o operador achar que a tela está quebrada. */}
        {canManage && (
          partnership.partnershipStatus === 'ACTIVE' ? (
            <Actions>
              <Button variant="secondary" onClick={openFeatureForm}>
                Criar destaque
              </Button>
            </Actions>
          ) : (
            <Suggestion>
              Só empresa com parceria <strong>ativa</strong> pode ser destacada. Destaque é
              exposição paga com a relação comercial, e destacar quem não é parceiro venderia
              posição a quem não tem relação nenhuma com a plataforma.
            </Suggestion>
          )
        )}
      </Section>

      <Section>
        <h4>
          <History size={16} /> Histórico
        </h4>
        {history.length === 0 && <Muted>Nenhuma mudança registrada.</Muted>}
        {history.map((event) => (
          <TimelineItem key={event.id}>
            <div className="when">{formatDate(event.occurredAt)}</div>
            <div className="what">
              {STATUS_LABELS[event.fromStatus]} → <strong>{STATUS_LABELS[event.toStatus]}</strong>
            </div>
            {/* O motivo é o conteúdo da linha, não um detalhe dela: sem ele o
                histórico responde "quando" e "quem" e não responde "por quê". */}
            <div className="why">{event.reason}</div>
          </TimelineItem>
        ))}
      </Section>

      <PromptModal
        isOpen={pending !== null}
        danger={pending !== null && DESTRUCTIVE.includes(pending)}
        title={pending ? ACTION_LABELS[pending] : ''}
        message={
          <>
            Descreva o motivo - ele fica no histórico e é o que responde “por que esta parceria
            mudou?” daqui a seis meses.
          </>
        }
        label="Motivo"
        placeholder="Ex.: contrato assinado na feira de Toledo"
        confirmLabel="Confirmar"
        loading={saving}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />

      <Modal
        isOpen={featureForm !== null}
        onClose={() => setFeatureForm(null)}
        title="Criar destaque"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFeatureForm(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={submitFeature}
              isLoading={saving}
              /* Motivo em branco não passa. Ele é o que responde "por que esta
                 empresa estava no topo em setembro?" daqui a seis meses - e é a
                 única pergunta que ninguém consegue responder depois. */
              disabled={
                !featureForm?.reason.trim() ||
                (featureForm?.scope === 'LOCALITY' && !featureForm?.localityId) ||
                (featureForm?.scope === 'CATEGORY' && !featureForm?.categoryId)
              }
            >
              Destacar
            </Button>
          </>
        }
      >
        {featureForm && (
          <>
            <Select
              label="Onde"
              value={featureForm.scope}
              onChange={(e) =>
                setFeatureForm({ ...featureForm, scope: e.target.value as MerchantFeatureScope })
              }
              options={(Object.keys(SCOPE_LABELS) as MerchantFeatureScope[]).map((scope) => ({
                value: scope,
                label: SCOPE_LABELS[scope],
              }))}
            />

            {featureForm.scope === 'LOCALITY' && (
              <Select
                label="Cidade"
                value={featureForm.localityId}
                onChange={(e) => setFeatureForm({ ...featureForm, localityId: e.target.value })}
                options={[
                  { value: '', label: 'Selecione…' },
                  ...localities.map((l) => ({ value: l.id, label: `${l.name} - ${l.state}` })),
                ]}
              />
            )}

            {featureForm.scope === 'CATEGORY' && (
              <Select
                label="Categoria"
                value={featureForm.categoryId}
                onChange={(e) => setFeatureForm({ ...featureForm, categoryId: e.target.value })}
                options={[
                  { value: '', label: 'Selecione…' },
                  ...categories.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            )}

            {/* O aviso de densidade (card 262). Informa, nunca bloqueia: pode
                haver bom motivo para lotar uma cidade numa semana de campanha,
                e quem decide isso é o comercial, não o formulário. O que o
                formulário deve é impedir que a decisão saia sem o número. */}
            {density && (
              <DensityNotice $crowded={density.activeInContext > 0}>
                {density.activeInContext === 0 ? (
                  <>
                    Nenhum destaque vigente neste contexto - este será o primeiro.
                  </>
                ) : (
                  <>
                    Este contexto já tem <strong>{density.activeInContext}</strong>{' '}
                    {density.activeInContext === 1 ? 'destaque vigente' : 'destaques vigentes'}
                    {density.scope !== 'PLATFORM' && density.activePlatformWide > 0 && (
                      <>
                        {' '}({density.activePlatformWide} {density.activePlatformWide === 1 ? 'dele vale' : 'deles valem'}{' '}
                        para a plataforma inteira e aparece em toda vitrine)
                      </>
                    )}
                    . O motor promove no máximo <strong>1 a cada {density.promotedEveryPositions} posições</strong>,
                    então a vitrine precisa de ao menos{' '}
                    <strong>{density.positionsForAllPromoted} empresas</strong> para promover todos, incluindo este.
                    {density.scheduledInContext > 0 && (
                      <>
                        {' '}Há ainda {density.scheduledInContext}{' '}
                        {density.scheduledInContext === 1 ? 'agendado' : 'agendados'} para entrar nesta mesma disputa.
                      </>
                    )}
                  </>
                )}
              </DensityNotice>
            )}

            <TextInput
              label="Começa em"
              type="datetime-local"
              value={featureForm.startsAt}
              onChange={(e) => setFeatureForm({ ...featureForm, startsAt: e.target.value })}
            />
            <TextInput
              label="Termina em"
              type="datetime-local"
              value={featureForm.endsAt}
              onChange={(e) => setFeatureForm({ ...featureForm, endsAt: e.target.value })}
            />
            <Muted>
              Em branco: começa agora e dura enquanto a parceria durar. Perder ou suspender a
              parceria encerra o destaque no mesmo ato.
            </Muted>

            <TextInput
              label="Motivo"
              value={featureForm.reason}
              onChange={(e) => setFeatureForm({ ...featureForm, reason: e.target.value })}
              placeholder="Ex.: campanha de setembro em Toledo"
              maxLength={500}
            />
          </>
        )}
      </Modal>

      <ConfirmModal
        isOpen={endingFeature !== null}
        danger
        title="Encerrar destaque"
        message={
          <>
            A empresa sai das posições de destaque agora. O período já cumprido fica no
            registro - nada é apagado.
          </>
        }
        confirmLabel="Encerrar"
        loading={saving}
        onConfirm={confirmEndFeature}
        onClose={() => setEndingFeature(null)}
      />
    </>
  );
};

export default MerchantPartnershipSection;

/* -------------------------------------------------------------------------- */

const Section = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.1rem;
  margin-bottom: 1rem;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.85rem;
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  svg {
    color: ${(p) => p.theme.primary};
  }
`;

const Field = styled.div`
  margin-bottom: 0.7rem;

  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.7rem;

  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const Muted = styled.div`
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.9rem;
`;

/**
 * O aviso de lotação da vitrine (card 262).
 *
 * Muda de cor com o número, e não com um limiar: não existe "cheio demais"
 * definido em lugar nenhum - o teto do motor é sobre posições, não sobre
 * destaques, e quantas posições uma cidade tem é coisa que o operador sabe e a
 * tela não. Âmbar a partir do primeiro destaque é o suficiente para tirar o
 * texto do modo decorativo; vermelho seria a tela emitindo um veredito
 * comercial que não é dela.
 */
const DensityNotice = styled.div<{ $crowded: boolean }>`
  margin: 0.75rem 0;
  padding: 0.6rem 0.75rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => (p.$crowded ? p.theme.warning : p.theme.info)}1a;
  border-left: 3px solid ${(p) => (p.$crowded ? p.theme.warning : p.theme.info)};
  color: ${(p) => p.theme.textPrimary};
  font-size: 0.8rem;
  line-height: 1.5;
`;

const Suggestion = styled.div`
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.warning}1a;
  color: ${(p) => p.theme.textPrimary};
  font-size: 0.8rem;
  line-height: 1.45;
`;

const TimelineItem = styled.div`
  border-left: 2px solid ${(p) => p.theme.border};
  padding: 0 0 0.8rem 0.75rem;
  margin-left: 0.2rem;

  &:last-child {
    padding-bottom: 0;
  }

  .when {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .what {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textPrimary};
  }
  .why {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
    margin-top: 0.15rem;
  }
`;
